import type { AzureSpeechSettingsPayload } from '@Application/Ai/AiConfiguration/Models/Actions/aiModelsFetched';

class UpdateAiModel {
	constructor(
		public readonly accountId: number,
		public readonly modelId: number,
		public readonly identifier: string,
		public readonly label: string,
		public readonly usages: string[],
		// `null` means "no override": the provider's own `apiVersion` applies. The server refuses a
		// non-null value when the model's provider is not `azure_openai`.
		public readonly apiVersion: string | null,
		// `null` means "no family": only meaningful for audio models. Always sent explicitly, `null`
		// included, so the server can distinguish "no family" from "leave the family untouched".
		public readonly family: string | null = null,
		// Provider-specific settings. `null` means "no settings" and erases those registered: the form
		// is filled from the stored value, so what it submits is always the whole intended state.
		public readonly providerOptions: Record<string, unknown> | null = null,
		// `null` for a text model. Always sent explicitly, `null` included.
		public readonly deliveryMode: string | null = null,
		public readonly transcriptionChunking: boolean = true,
		// `null` means "the server's own value applies". Always sent explicitly, `null` included.
		public readonly transcriptionChunkMaxSeconds: number | null = null,
		public readonly transcriptionConcurrency: number | null = null,
		// Prices in euros, `null` meaning "not set". Always sent explicitly, `null` included: the form is
		// filled from the stored values, so what it submits is the whole intended state.
		public readonly inputPricePerMillionTokens: number | null = null,
		public readonly outputPricePerMillionTokens: number | null = null,
		public readonly cachedInputPricePerMillionTokens: number | null = null,
		public readonly cacheWritePricePerMillionTokens: number | null = null,
		public readonly pricePerAudioMinute: number | null = null,
		public readonly billedPerStartedMinute: boolean = false,
		// A whole object or absent: absent keeps the registered settings. Never sent on creation, where
		// the server applies its own defaults.
		public readonly azureSpeechSettings: AzureSpeechSettingsPayload | null = null,
	) {
		Object.freeze(this);
	}

	// A model never changes provider: redeclaring it is the way to switch. `accountId` and `modelId`
	// are route identifiers, and `providerId` is deliberately absent — sending it makes the server
	// respond `400`, `forbidNonWhitelisted` being global.
	toJSON(): Record<string, unknown> {
		const json: Record<string, unknown> = {
			identifier: this.identifier,
			label: this.label,
			usages: this.usages,
			apiVersion: this.apiVersion,
			family: this.family,
			deliveryMode: this.deliveryMode,
			transcriptionChunking: this.transcriptionChunking,
			transcriptionChunkMaxSeconds: this.transcriptionChunkMaxSeconds,
			transcriptionConcurrency: this.transcriptionConcurrency,
			inputPricePerMillionTokens: this.inputPricePerMillionTokens,
			outputPricePerMillionTokens: this.outputPricePerMillionTokens,
			cachedInputPricePerMillionTokens: this.cachedInputPricePerMillionTokens,
			cacheWritePricePerMillionTokens: this.cacheWritePricePerMillionTokens,
			pricePerAudioMinute: this.pricePerAudioMinute,
			billedPerStartedMinute: this.billedPerStartedMinute,
			providerOptions: this.providerOptions,
		};

		if (this.azureSpeechSettings !== null) {
			json.azureSpeechSettings = this.azureSpeechSettings;
		}

		return json;
	}
}

export default UpdateAiModel;
